import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import AppointmentNotesModal from '../components/AppointmentNotesModal';
import { toast } from 'react-toastify';

function DoctorProfileView() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [callType, setCallType] = useState('VIDEO');
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [showAllReviews, setShowAllReviews] = useState(false);

  useEffect(() => {
    fetchData();
  }, [doctorId, callType]);

  const fetchData = async () => {
    try {
      const [docRes, slotsRes] = await Promise.all([
        api.get(`doctor/profile/${doctorId}/`),
        api.get(`appointments/user/slots/${doctorId}/`, { params: { call_type: callType } })
      ]);
      setDoctor(docRes.data);
      setSlots(slotsRes.data);
      setSelectedSlot(null);

      if (slotsRes.data && slotsRes.data.length > 0) {
        const todayStr = new Date().toISOString().split('T')[0];
        const availableDates = [...new Set(slotsRes.data.map(s => s.date))].sort();

        if (availableDates.includes(todayStr)) {
          setSelectedDate(todayStr);
        } else {
          setSelectedDate(availableDates[0]);
        }
      } else {
        setSelectedDate(null);
      }
      // fetch reviews list
      try {
        const rev = await api.get(`ratings/doctor/${doctorId}/`);
        setReviews(rev.data || []);
      } catch (e) {
        console.error('Error fetching reviews:', e);
        setReviews([]);
      }
    } catch (err) {
      console.error("Error fetching doctor profile:", err);
      if (err.response?.status === 401) navigate('/user/login');
    } finally {
      setLoading(false);
    }
  };

  const handleBook = () => {
    if (!selectedSlot) {
      toast.warning("Please select a time slot first.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    // Open Notes & Medical Report Modal before payment
    setShowNotesModal(true);
  };

  const handleProceedToPayment = ({ patientNotes, medicalReportFiles, medicalReportFile }) => {
    setShowNotesModal(false);
    const files = Array.isArray(medicalReportFiles) && medicalReportFiles.length
      ? medicalReportFiles
      : (medicalReportFile ? [medicalReportFile] : []);
    navigate(`/user/payment?slot_id=${selectedSlot.id}`, {
      state: {
        patientNotes,
        medicalReportFiles: files,
        medicalReportFile: files[0] || null,
      }
    });
  };

  // Helper function to get image URL
  const getImageUrl = (image, type = 'Doctor') => {
    if (!image) return `https://via.placeholder.com/300x300?text=${type}`;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }

    // If it already has /media/ at start
    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }

    // Add /media/ prefix for paths like "doctor_images/filename.png"
    return `${apiUrl}/media/${image}`;
  };

  // Filter only upcoming slots (future date/time)
  const upcomingSlots = slots.filter(slot => {
    if (!slot.date || !slot.start_time) return false;
    const slotDateTime = new Date(`${slot.date} ${slot.start_time}`);
    const now = new Date();
    return slotDateTime >= now; // Only include future slots
  });

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <UserNavbar />

      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">

            <div className="d-flex align-items-center mb-4">
              <button className="btn btn-link text-decoration-none text-muted p-0 me-3" onClick={() => navigate(-1)}>
                <i className="bi bi-arrow-left fs-4"></i>
              </button>
              <h2 className="fw-bold mb-0">Doctor Profile</h2>
            </div>

            {/* DOCTOR PROFILE CARD */}
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
              <div className="row g-0">
                <div className="col-md-4 bg-primary text-white p-5 text-center d-flex flex-column align-items-center justify-content-center">
                  <div className="bg-white rounded-circle p-1 mb-4">
                    <img
                      src={getImageUrl(doctor?.image)}
                      alt={doctor?.name}
                      className="rounded-circle object-fit-cover"
                      style={{ width: '180px', height: '180px' }}
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/300x300?text=Doctor";
                      }}
                    />
                  </div>
                  <h3 className="fw-bold mb-1">{doctor?.name}</h3>
                  <p className="badge bg-light text-primary fs-6 mb-2">{doctor?.specialization}</p>
                  {/* Rating summary */}
                  <div className="d-flex align-items-center gap-3 mt-2">
                    <div className="d-flex align-items-center">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`bi ${i < Math.round((doctor?.average_rating || 0)) ? 'bi-star-fill text-warning' : 'bi-star text-white'}`} />
                      ))}
                    </div>

                  </div>
                  {/* Availability Status */}
                  <div className="mb-3">
                    {slots.length > 0 ? (
                      <span className="badge bg-success rounded-pill px-3 py-2">
                        <i className="bi bi-check-circle me-1"></i>Available
                      </span>
                    ) : (
                      <span className="badge bg-secondary rounded-pill px-3 py-2">
                        <i className="bi bi-x-circle me-1"></i>Unavailable
                      </span>
                    )}
                  </div>
                  <div className="d-flex flex-column gap-2 w-100 px-3">
                    <button
                      className="btn btn-light rounded-pill px-4 fw-bold"
                      onClick={() => navigate(`/user/book-appointment/${doctorId}`)}
                      disabled={slots.length === 0}
                    >
                      <i className="bi bi-calendar-check me-2"></i>Book Appointment
                    </button>
                  </div>
                </div>

                <div className="col-md-8 p-5">
                  <div className="row g-4">
                    <div className="col-12">
                      <h5 className="fw-bold border-bottom pb-2 mb-3">Professional Summary</h5>
                      <p className="text-muted">
                        {doctor?.name} is a highly qualified {doctor?.specialization} with over {doctor?.experience} years of experience in providing exceptional medical care.
                      </p>
                    </div>

                    <div className="col-md-6">
                      <label className="text-muted small text-uppercase fw-bold d-block mb-1">Qualification</label>
                      <p className="fw-medium mb-0">{doctor?.qualification || '—'}</p>
                    </div>

                    <div className="col-md-6">
                      <label className="text-muted small text-uppercase fw-bold d-block mb-1">Specialization</label>
                      <p className="fw-medium mb-0">{doctor?.specialization || '—'}</p>
                    </div>

                    <div className="col-md-6">
                      <label className="text-muted small text-uppercase fw-bold d-block mb-1">Experience</label>
                      <p className="fw-medium mb-0">{doctor?.experience ? `${doctor.experience} Years` : '—'}</p>
                    </div>

                    <div className="col-md-6">
                      <label className="text-muted small text-uppercase fw-bold d-block mb-1">Clinic Address</label>
                      <p className="fw-medium mb-0">{doctor?.address || 'Available for Online Consultation'}</p>
                    </div>

                    <div className="col-md-6">
                      <label className="text-muted small text-uppercase fw-bold d-block mb-1">Phone Number</label>
                      <p className="fw-medium mb-0">{doctor?.phone_number || '—'}</p>
                    </div>

                    <div className="col-md-6">
                      <label className="text-muted small text-uppercase fw-bold d-block mb-1">Email</label>
                      <p className="fw-medium mb-0">{doctor?.email || '—'}</p>
                    </div>

                    <div className="col-12 border-top pt-4 mt-4">
                      <div className="d-flex align-items-center p-3 bg-info bg-opacity-10 border border-info border-opacity-25 rounded-4">
                        <i className="bi bi-info-circle-fill text-info fs-4 me-3"></i>
                        <div>
                          <h6 className="fw-bold text-info mb-1">Verified Professional</h6>
                          <p className="small text-muted mb-0">This doctor's credentials have been verified by FITNOVA Health.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* AVAILABLE SLOTS SECTION */}
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0 d-flex align-items-center">
                    <i className="bi bi-calendar-check me-2 text-primary"></i>
                    Available Time Slots
                  </h5>
                  {slots.length > 0 ? (
                    <span className="badge bg-success">
                      <i className="bi bi-check-circle me-1"></i>Available
                    </span>
                  ) : (
                    <span className="badge bg-secondary">
                      <i className="bi bi-x-circle me-1"></i>Unavailable
                    </span>
                  )}
                </div>

                <div className="mb-4">
                  <p className="text-muted small text-uppercase fw-bold mb-2">Consultation Type</p>
                  <div className="d-flex gap-2">
                    {['VIDEO', 'AUDIO'].map((type) => (
                      <button
                        key={type}
                        type="button"
                        className={`btn flex-fill rounded-pill py-2 fw-semibold ${callType === type ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setCallType(type)}
                      >
                        <i className={`bi ${type === 'VIDEO' ? 'bi-camera-video' : 'bi-telephone'} me-2`}></i>
                        {type === 'VIDEO' ? 'Video Call' : 'Audio Call'}
                      </button>
                    ))}
                  </div>
                </div>

                {upcomingSlots.length === 0 ? (
                  <div className="text-center py-5 bg-light rounded-4">
                    <i className="bi bi-calendar-x fs-1 text-muted mb-3 d-block"></i>
                    <p className="text-muted fw-medium">No available slots at the moment.</p>
                    <p className="small text-muted">This doctor is currently unavailable. All slots are either booked, inactive, or expired.</p>
                  </div>
                ) : (
                  <>
                    {/* Horizontal Date Picker */}
                    <div className="mb-4 pb-2">
                      <div className="d-flex overflow-auto pb-3 gap-3 date-filter-scroll" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {[...new Set(upcomingSlots.map(s => s.date))].sort().map(date => {
                          const dateObj = new Date(date);
                          const isToday = date === new Date().toISOString().split('T')[0];
                          const isSelected = selectedDate === date;
                          return (
                            <button
                              key={date}
                              onClick={() => {
                                setSelectedDate(date);
                                setSelectedSlot(null);
                              }}
                              className={`btn border-0 rounded-4 p-3 d-flex flex-column align-items-center transition-all ${isSelected
                                ? 'bg-primary text-white shadow'
                                : 'bg-white text-dark shadow-sm'
                                }`}
                              style={{ minWidth: '94px', flexShrink: 0 }}
                            >
                              <span className={`small fw-bold mb-1 ${isSelected ? 'text-white-50' : 'text-primary uppercase'}`}>
                                {isToday ? 'TODAY' : dateObj.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase()}
                              </span>
                              <span className="fs-4 fw-bold mb-0">
                                {dateObj.getDate()}
                              </span>
                              <span className="small fw-medium">
                                {dateObj.toLocaleDateString(undefined, { month: 'short' })}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Slots for Selected Date */}
                    <div className="bg-light p-4 rounded-4 mb-4">
                      <h6 className="fw-bold mb-3 d-flex align-items-center text-dark small text-uppercase">
                        <i className="bi bi-clock me-2 text-primary"></i>
                        {selectedDate ? `Available on ${new Date(selectedDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}` : 'Select a date'}
                      </h6>
                      <div 
                        className="pe-2 custom-slots-scroll" 
                        style={{ 
                          maxHeight: '200px', 
                          overflowY: 'auto',
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                          gap: '10px'
                        }}
                      >
                        {upcomingSlots
                          .filter(s => s.date === selectedDate)
                          .map(slot => (
                            <button
                              key={slot.id}
                              className={`btn rounded-pill py-2 px-3 fw-bold transition-all text-center ${selectedSlot?.id === slot.id ? 'btn-primary shadow-sm scale-105' : 'btn-outline-primary bg-white'
                                }`}
                              onClick={() => setSelectedSlot(slot)}
                            >
                              {slot.start_time.slice(0, 5)} - {slot.end_time.slice(0, 5)}
                            </button>
                          ))}
                      </div>
                    </div>

                    {/* Booking Section */}
                    {selectedSlot && (
                      <div className="mt-5 pt-4 border-top">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <div>
                            <p className="text-muted small mb-0 text-uppercase fw-bold">Selected Slot</p>
                            <h6 className="fw-bold mb-0">
                              {new Date(selectedSlot.date).toLocaleDateString(undefined, {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })} at {selectedSlot.start_time}
                            </h6>
                          </div>
                          <div className="text-end">
                            <p className="text-muted small mb-0 text-uppercase fw-bold">{callType === 'AUDIO' ? 'Audio' : 'Video'} Fee</p>
                            <h6 className="fw-bold mb-0 text-success">
                              ${(() => {
                                const fee = callType === 'AUDIO'
                                  ? doctor?.audio_consultation_fees
                                  : doctor?.consultation_fees;
                                return fee != null ? parseFloat(fee).toFixed(2) : '0.00';
                              })()} + $5.00
                            </h6>
                          </div>
                        </div>

                        <button
                          className="btn btn-primary btn-lg w-100 rounded-3 fw-bold d-flex align-items-center justify-content-center"
                          disabled={bookingLoading}
                          onClick={handleBook}
                        >
                          {bookingLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Booking...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-check-circle me-2"></i>
                              Confirm Booking Request
                            </>
                          )}
                        </button>
                        <p className="small text-muted text-center mt-3">
                          <i className="bi bi-shield-check me-1"></i>
                          Secure appointment booking with FITNOVA Health.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* REVIEWS SECTION */}
            <div className="card border-0 shadow-sm rounded-4 mt-4">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0 d-flex align-items-center">
                    <i className="bi bi-chat-left-text me-2 text-primary"></i>
                    Patient Reviews
                  </h5>
                  <small className="text-muted">{reviews.length} reviews</small>
                </div>

                {reviews.length === 0 ? (
                  <div className="text-center py-5 bg-light rounded-4">
                    <i className="bi bi-star fs-1 text-muted mb-3 d-block"></i>
                    <p className="text-muted fw-medium">No reviews yet. Be the first to review.</p>
                  </div>
                ) : (
                  <div>
                    <div className="list-group mb-4">
                      {reviews.slice(0, showAllReviews ? reviews.length : 3).map(r => (
                        <div key={r.id} className="list-group-item border-0 mb-4 shadow-sm rounded-4 p-4 transition-all hover-shadow-sm">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center">
                              <img
                                src={getImageUrl(r.user_image, 'U')}
                                alt={r.user_name}
                                className="rounded-circle me-3 shadow-sm"
                                style={{ width: '56px', height: '56px', objectFit: 'cover' }}
                                onError={(e) => {
                                  e.target.src = "https://via.placeholder.com/100x100?text=U";
                                }}
                              />
                              <div>
                                <h6 className="fw-bold mb-0">{r.user_name || 'Anonymous User'}</h6>
                                <div className="small text-muted">
                                  <i className="bi bi-calendar-event me-1"></i>
                                  {new Date(r.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                </div>
                              </div>
                            </div>
                            <div className="bg-warning bg-opacity-10 text-warning px-3 py-1 rounded-pill small fw-bold d-flex align-items-center">
                              <div className="me-2">
                                {[...Array(5)].map((_, i) => (
                                  <i key={i} className={`bi ${i < Math.round(r.rating) ? 'bi-star-fill text-warning' : 'bi-star'} small me-1`}></i>
                                ))}
                              </div>
                              <span className="ms-1">{r.rating}.0</span>
                            </div>
                          </div>
                          {r.review_text && (
                            <p className="text-secondary mb-0 p-3 bg-light rounded-4 border-start border-primary border-4">
                              "{r.review_text}"
                            </p>
                          )}
                        </div>
                      ))}
                    </div>

                    {reviews.length > 3 && (
                      <div className="text-center">
                        <button
                          className="btn btn-outline-primary rounded-pill px-5 fw-bold"
                          onClick={() => setShowAllReviews(!showAllReviews)}
                        >
                          {showAllReviews ? (
                            <><i className="bi bi-chevron-up me-2"></i>Show Less</>
                          ) : (
                            <><i className="bi bi-chevron-down me-2"></i>View All {reviews.length} Reviews</>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SYMPTOMS & MEDICAL REPORT MODAL */}
      <AppointmentNotesModal
        show={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        onProceed={handleProceedToPayment}
      />

      <style>{`
        .transition-all { transition: all 0.3s ease; }
        .scale-105 { transform: scale(1.05); }
        .date-filter-scroll::-webkit-scrollbar { display: none; }
        .date-filter-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .custom-slots-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .custom-slots-scroll::-webkit-scrollbar-track {
          background: #e9ecef;
          border-radius: 10px;
        }
        .custom-slots-scroll::-webkit-scrollbar-thumb {
          background: #0d6efd;
          border-radius: 10px;
        }
        .custom-slots-scroll::-webkit-scrollbar-thumb:hover {
          background: #0b5ed7;
        }
      `}</style>
    </div>
  );
}

export default DoctorProfileView;
